import { useInfiniteQuery } from 'react-query'
import { getJSON } from 'common/services/api'
import { DeviceRecord } from '../types'

interface DeviceRecordsListResponse {
  data: DeviceRecord[]
  pagination: {
    currentPage: number
    itemsPerPage: number
    totalItems: number
    totalPages: number
  }
}

interface UseDeviceRecordsListParams {
  residentId: string
  limit?: number
}

interface UseDeviceRecordsListReturn {
  deviceRecords: DeviceRecord[]
  isLoading: boolean
  refetch: () => void
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
}

export const useDeviceRecordsList = ({
  residentId,
  limit = 50
}: UseDeviceRecordsListParams): UseDeviceRecordsListReturn => {
  const fetchDeviceRecords = async ({ pageParam = 1 }) => {
    const url = `/v6/residents/${residentId}/device-records?limit=${limit}&page=${pageParam}`

    const response = (await getJSON({ url })) as DeviceRecordsListResponse

    return {
      deviceRecords: response.data || [],
      totalCount: response.pagination?.totalItems || 0,
      currentPage: response.pagination?.currentPage || pageParam,
      totalPages: response.pagination?.totalPages || 1
    }
  }

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery(['device-records', residentId], fetchDeviceRecords, {
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce(
        (total, page) => total + page.deviceRecords.length,
        0
      )

      if (loadedCount >= lastPage.totalCount) {
        return undefined
      }

      if (lastPage.currentPage >= lastPage.totalPages) {
        return undefined
      }

      return lastPage.currentPage + 1
    },
    enabled: !!residentId
  })

  const deviceRecords = data?.pages.flatMap((page) => page.deviceRecords) || []

  return {
    deviceRecords,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage
  }
}
